/**
 * „Twój kod trenera" — the card that stands beside the Profil tab.
 *
 * It draws a fact the trainer reads aloud to someone, so the code is large, letter-spaced and
 * tabular, and the one control copies it. Nothing here writes.
 *
 * ── The copy is the mobile app's, word for word ─────────────────────────────────────────────
 * `coaching.code.title`, `.sub`, `.copied` and `.unavailable` are upstream keys, read unprefixed:
 * the phone already says all four of these sentences about the same code, and a second wording on
 * this side is how two surfaces come to describe one thing differently. Only the button's own
 * „Kopiuj kod" is web copy, because upstream's is „Kopiuj" and the canon writes the noun.
 *
 * ── Three states and a reserve ──────────────────────────────────────────────────────────────
 * The code's line is sized before the read answers, so the bar and the digits occupy the same box.
 * `trainerCode === null` on a successful read is its own case — a trainer whose row carries none —
 * and it says so rather than drawing an empty line where a code should be.
 */

import { useTranslation } from 'react-i18next';

import ink from '~/shared/ui/inkButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { CopyGlyph } from '~/shared/ui/FormGlyphs';

import type { AccountProfileApi } from '../lib/useAccountProfile';
import screen from './AccountScreen.module.css';
import styles from './TrainerCodeCard.module.css';

interface TrainerCodeCardProps {
  /**
   * The Profil tab's own API object, as the other side panels take theirs. The card and the form
   * read ONE response — six loose props were six chances for the two to be handed different halves
   * of it.
   */
  readonly profile: AccountProfileApi;
}

export function TrainerCodeCard({ profile }: TrainerCodeCardProps) {
  const { t } = useTranslation();
  /** `null` when the read answered and the trainer's row carries no code. */
  const { state, waiting, skeleton, reveal, trainerCode: code, onRetry, announce } = profile;

  return (
    <div className={screen.sidePanel}>
      {/* The card's WAIT, which is what the bar below lives by: it outlives the read by the
        * gate's minimum life, and until it goes the line is still about to change. */}
      <div className={screen.card} aria-busy={waiting}>
        <h2 className={screen.cardTitle}>{t('coaching.code.title')}</h2>
        <p className={screen.cardBody}>{t('coaching.code.sub')}</p>

        {/* The line is sized in the stylesheet, so the bar, the code and the refusal all stand in
          * one box and the answer landing moves nothing under it. */}
        <div className={styles.codeLine}>
          {/* The GATE decides, not the read: a code that lands inside the 150ms grace draws no bar
            * at all, and one that is drawn stays for its 300ms so it cannot flicker. The line's
            * height is the stylesheet's in every case, so this switches ink and never geometry.
            *
            * The WORDS wait on the same verdict the bar does. Keyed on the read, the code was drawn
            * the instant it arrived and stood ON TOP of a bar the gate was still holding, for the
            * length of the hand-over. The sentence for a listener waits with them, beside the
            * `aria-busy` above it — one value for the branch, the attribute and the words. */}
          {skeleton && <span className={`${String(skel.bar)} ${String(styles.codeBar)}`} />}
          {waiting && <span className={hidden.srOnly}>{t('coaching.code.title')}</span>}
          {waiting ? null : code === null ? (
            <span className={styles.unavailable}>{t('coaching.code.unavailable')}</span>
          ) : (
            <span className={`${String(styles.code)} ${reveal}`}>{code}</span>
          )}
        </div>

        {!waiting && state === 'error' ? (
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(styles.action)}`}
            onClick={onRetry}
          >
            {t('common.retry')}
          </button>
        ) : (
          <button
            type="button"
            className={`${String(ink.ink)} ${String(styles.action)}`}
            // The code is what the button copies, so with no code there is nothing to press —
            // refused rather than absent, so the card keeps its shape in all three states.
            aria-disabled={code === null ? 'true' : undefined}
            onClick={
              code === null
                ? undefined
                : () => {
                    void navigator.clipboard.writeText(code).then(
                      () => {
                        announce(t('inviteCustomer.codeCopied'));
                      },
                      () => {
                        // A clipboard a browser refused is not an error worth a banner on a card
                        // whose code is already on screen and selectable.
                        announce(t('coaching.code.unavailable'));
                      },
                    );
                  }
            }
          >
            <CopyGlyph className={styles.actionIcon} />
            {t('web:account.code.copy')}
          </button>
        )}

        {/* The code as a sentence, for a listener who cannot read the letter-spaced digits. */}
        {code !== null && (
          <span className={hidden.srOnly}>{t('coaching.code.a11y', { code })}</span>
        )}
      </div>
    </div>
  );
}
